import {
    Accomodation,
    AnalysisResponse, RateMapping,
    TransactionPerformanceDTO, TransactionPerformanceGroupedResponse
} from "../../../types";
import {useEffect, useRef, useState} from "react";
import dayjs from "dayjs";
import {useMutation, useQuery} from "react-query";
import {
    analyzeAccommodation,
    getAccommodationStats,
    getAccomodationApiChannels,
    getAnalyzeTransactionData
} from "../../../shared/queries";
import {DateInput} from "@heroui/date-input";
import {parseDateTime} from "@internationalized/date";
import {Button} from "@heroui/button";
import {formatCurrency} from "../../../shared/utils";
import toast from "react-hot-toast";
import {Select, SelectItem, Spinner} from "@heroui/react";

const POLLING_INTERVAL_MS = 5000;
const TIMEOUT_MS = 5 * 60 * 1000;

export default function HotelAnalysis ({ accommodation } : { accommodation : Accomodation }) {

    const [startDate, setStartDate] = useState<string>(dayjs().utc().subtract(60, 'days').startOf('day').format('YYYY-MM-DDTHH:mm:ss'));
    const [endDate, setEndDate] = useState<string>(dayjs().utc().endOf('day').format('YYYY-MM-DDTHH:mm:ss'));
    const [currentChannel, setCurrentChannel] = useState<number | undefined>()

    const [processedChunks, setProcessedChunks] = useState<number>(0)
    const [totalChunks, setTotalChunks] = useState<number>(0)

    const [pollingResult, setPollingResult] = useState<TransactionPerformanceGroupedResponse | undefined>(undefined)
    const [error, setError] = useState<string | null>(null);
    const [isPolling, setIsPolling] = useState(false);
    const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
    const startTimeRef = useRef<number>(0);

    const [analysisResponse, setAnalysisResponse] = useState<AnalysisResponse | undefined>();

    const { data, refetch } = useQuery(['accommodationAnalysis', accommodation.idAccomodation], () => getAccommodationStats(
        startDate,
        endDate,
        accommodation.idAccomodation || 0
    ), {
        enabled: (accommodation.idAccomodation !== null && accommodation.idAccomodation !== undefined)
    })

    const {
        data: connectedChannels
    } = useQuery(['accomodationApiChannel', accommodation.idAccomodation], () => getAccomodationApiChannels(accommodation.idAccomodation || 0), {
        enabled: (accommodation.idAccomodation !== null && accommodation.idAccomodation !== undefined)
    })

    const startAnalysis = useMutation((data: {
        idAccommodation: number,
        idChannel: number
    }) => analyzeAccommodation(data.idAccommodation, data.idChannel), {
        onSuccess: (data) => {
            toast.success('Analisi inviata')
            setAnalysisResponse(data)
            startPolling(data.transactionId)
        },
        onError: () => {
            toast.error('Impossibile avviare l\'analisi')
        }
    })

    type GroupedData = {
        totalBookings: number;
        channels: {
            sourceChannel: string;
            transactions: TransactionPerformanceDTO[];
            channelBookingCount: number;
            bookingPercentage: number;
        }[];
    };

    function groupByBookingRateAndSourceChannelWithTotals(
        transactions: TransactionPerformanceDTO[],
        rateMapping: RateMapping[]
    ): Record<string, GroupedData> {
        const result: Record<string, GroupedData> = {};

        transactions.forEach((tx) => {
            let key = 'bookingRate' as keyof TransactionPerformanceDTO

            if (tx.bookingRateName !== undefined && tx.bookingRateName !== null && tx.bookingRateName !== '') {
                key = 'bookingRateName'
            }

            let searchKey = tx[key] as any

            console.log(searchKey, rateMapping)

            if (rateMapping.length > 0 && key === 'bookingRate') {
                const matchedRate = rateMapping.find(r => r.rateId.toString().toLowerCase() === tx[key])
                if (matchedRate) {
                    searchKey = matchedRate.rateName
                }
            }

            if (!result[searchKey]) {
                result[searchKey] = {
                    totalBookings: 0,
                    channels: [],
                };
            }

            let channelData = result[searchKey].channels.find(
                (c) => c.sourceChannel === tx.sourceChannel
            );

            if (!channelData) {
                channelData = {
                    sourceChannel: tx.sourceChannel,
                    transactions: [],
                    channelBookingCount: 0,
                    bookingPercentage: 0,
                };
                result[searchKey].channels.push(channelData);
            }

            channelData.transactions.push(tx);
            channelData.channelBookingCount += tx.bookingsCount;
            result[searchKey].totalBookings += tx.bookingsCount;
        });

        Object.values(result).forEach((group) => {
            group.channels.forEach((channel) => {
                if (group.totalBookings > 0) {
                    channel.bookingPercentage =
                        (channel.channelBookingCount / group.totalBookings) * 100;
                }
            });
        });

        return result;
    }

    const startPolling = (txId: string) => {
        setPollingResult(undefined)
        setError(null)
        setIsPolling(true)
        startTimeRef.current = Date.now()

        const poll = async () => {
            try {
                if (!accommodation || !accommodation.idAccomodation) return;
                const response = await getAnalyzeTransactionData(accommodation.idAccomodation, txId)
                console.log("Received data:", response);
                if (response && response.length > 0 && response[0].items.length > 0) {
                    const totalChunks = response[0].items[0].totalChunks
                    const processedChunks = response[0].processedChunks
                    setProcessedChunks(processedChunks)
                    setTotalChunks(totalChunks)
                    setPollingResult(response)
                    if (processedChunks >= totalChunks) stopPolling()
                } else {
                    const elapsed = Date.now() - startTimeRef.current
                    if (elapsed >= TIMEOUT_MS) {
                        setError("Polling timed out after 3 minutes.")
                        stopPolling()
                    }
                }
            } catch (err) {
                console.error(err)
                setError("Error during polling: " + String(err))
                stopPolling()
            }
        };

        poll();

        intervalIdRef.current = setInterval(poll, POLLING_INTERVAL_MS);

        timeoutIdRef.current = setTimeout(() => {
            setError("Polling timed out after 3 minutes.");
            stopPolling();
        }, TIMEOUT_MS);
    }

    const stopPolling = () => {
        if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current)
            intervalIdRef.current = null
        }
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current)
            timeoutIdRef.current = null
        }
        setIsPolling(false)
    }

    function handleAnalysis () {
        if (accommodation && accommodation.idAccomodation && currentChannel) {
            setAnalysisResponse(undefined)
            startAnalysis.mutate({
                idAccommodation: accommodation.idAccomodation,
                idChannel: currentChannel
            })
        }
    }

    useEffect(() => {
        refetch()
    }, []);

    return (
        <div>

            <div className={'mt-8'}>
                <div className="flex items-center space-x-4 border-b pb-8">
                    <div className={'text-4xl '}>
                        🤫
                    </div>
                    <div className={'flex flex-col'}>
                        <div className={'text-xl font-semibold text-slate-800'}>Accommodation Analysis Wizardry</div>
                        <div className={'text-slate-600'}>
                            <i><strong>psst!</strong> this data are meant to kept secret. From great power comes great responsibility.</i>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-8">
                <div className="text-lg font-medium text-slate-800">
                    Analizza struttura
                </div>
                <div className={'mt-2 text-slate-700'}>
                    avviando l'analisi di una struttura il sistema leggerà il canale associaato e tornerà indietro di 90 giorni. L'analisi
                    potrebbe richiedere alcuni minuti. L'analisi può includere i canali non diretti come OTA solo se il sistema che si
                    analizza colleziona prenotazioni anche da questi canali (es: ChannelManager, PMS, etc).
                    Non tutti i sistemi possono essere analizzati.
                </div>

                <div className={'mt-6 mb-6'}>
                    <Select
                        className="max-w-xs"
                        label="Select channel"
                        onSelectionChange={item => {
                            if (item && item.currentKey) {
                                setCurrentChannel(parseInt(item.currentKey))
                            }
                        }}
                    >
                        {
                            connectedChannels  ? connectedChannels.filter(c => c.apiChannel.idApiChannel !== 13).map((channel) => (
                            <SelectItem key={channel.idAccomodationApi}>{channel.apiChannel.apiChannelName}</SelectItem>
                            )) : <></>
                        }
                    </Select>
                </div>

                <div className={'mt-4'}>
                    <Button isLoading={startAnalysis.isLoading} onPress={() => handleAnalysis()}>Analizza</Button>
                </div>

                {
                    (isPolling || error || pollingResult) &&
                    <div className="bg-slate-800 text-white rounded-md mt-6 flex flex-col space-y-1">
                        <div className={'border-b border-slate-500 p-2'}>
                            Started analysis: {analysisResponse?.transactionId}
                        </div>
                        {
                            isPolling &&
                            <div className={'flex items-center space-x-2 p-2'}>
                                <div>
                                    <Spinner size={'sm'}/>
                                </div>
                                {
                                    processedChunks > 0 ?
                                        <div>
                                            it may take a while, processed: {processedChunks} of {totalChunks}
                                        </div>
                                        :
                                        <div>
                                            Server is picking up your request... wait 1 minute, if nothing change come back later, we will notify you on Slack
                                        </div>
                                }
                            </div>
                        }

                        {
                            error &&
                            <div className={'p-2'}>Request is taking longer then expected, please come back later.</div>
                        }
                    </div>
                }
            </div>


            <div className={'mt-10 mb-4 text-lg font-medium'}>
                Storico analisi
            </div>
            <div className="flex space-x-4 items-center">
                <div>
                    <DateInput
                        className="max-w-sm"
                        label={"From date"}
                        size={'sm'}
                        value={parseDateTime(startDate)}
                        onChange={val => {
                            if (val) {
                                setStartDate(dayjs(val.toDate('Europe/Rome')).format('YYYY-MM-DDTHH:mm:ss'))
                            }
                        }}
                    />
                </div>
                <div>
                    <DateInput
                        className="max-w-sm"
                        label={"To date"}
                        size={'sm'}
                        value={parseDateTime(endDate)}
                        onChange={val => {
                            if (val) {
                                setEndDate(dayjs(val.toDate('Europe/Rome')).format('YYYY-MM-DDTHH:mm:ss'))
                            }
                        }}
                    />
                </div>
                <div>
                    <Button onPress={() => refetch()}>Refresh</Button>
                </div>
            </div>

            <div className={'mt-10 flex flex-col space-y-4'}>
                {
                    data?.map(item => {
                        return (
                            <div className={'border border-slate-300 rounded-md overflow-hidden'}>
                                <div className={'flex justify-between items-center p-4 bg-slate-50'}>
                                    <div className={'text-lg font-semibold'}>{item.txId}</div>
                                    <div className={'text-slate-500'}>sent on {dayjs(item.items[0].txCreatedAt).format('DD/MM/YYYY HH:mm')}</div>
                                </div>

                                <div className="space-y-8 p-6">
                                    {Object.entries(groupByBookingRateAndSourceChannelWithTotals(item.items, item.rateMapping)).map(([bookingRate, group]) => (
                                        <div key={bookingRate}>
                                            <h2 className="text-lg text-gray-800 mb-4">
                                                <span className={'font-bold'}>Rate: {bookingRate}</span>
                                                <span className="text-sm text-gray-500 ml-2">
                                                  ({group.totalBookings} total bookings)
                                                </span>
                                            </h2>

                                            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                                                {group.channels.map((channel) => (
                                                    <div
                                                        key={channel.sourceChannel}
                                                        className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow duration-300"
                                                    >
                                                        <div className="flex items-center justify-between mb-3">
                                                          <span className="text-sm text-gray-500 uppercase tracking-wide">
                                                            {channel.sourceChannel}
                                                          </span>
                                                            <span className="text-xs text-gray-400">
                                                            {channel.channelBookingCount} bookings
                                                            </span>
                                                        </div>

                                                        <div className="flex items-center gap-2 mb-3">
                                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                                <div
                                                                    className="bg-blue-500 h-2 rounded-full"
                                                                    style={{
                                                                        width: `${channel.bookingPercentage}%`,
                                                                    }}
                                                                ></div>
                                                            </div>
                                                            <span className="text-xs text-gray-600 font-semibold w-12 text-right">
                                                                {channel.bookingPercentage.toFixed(1)}%
                                                              </span>
                                                        </div>

                                                        <ul className="space-y-1">
                                                            {channel.transactions.map((tx) => (
                                                                <li
                                                                    key={tx.idTransactionPerformance}
                                                                    className="flex justify-between text-xs text-gray-700 border-b border-dashed border-gray-200 pb-1"
                                                                >
                                                                    <span className="font-mono">Summary</span>
                                                                    <span>
                                                                        Total bookings: {tx.bookingsCount} · ADR €{formatCurrency(tx.avgBookingTotal)} · LOS {Math.round(tx.avgLos * 100) / 100}
                                                                      </span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full border border-gray-300">
                                            <thead className="bg-gray-100">
                                            <tr>
                                                <th className="px-4 py-2 border">Rate ID</th>
                                                <th className="px-4 py-2 border">Rate Name</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {item.rateMapping.map((item) => (
                                                <tr key={item.idRateMapping} className="hover:bg-gray-50">
                                                    <td className="px-4 py-2 border">{item.rateId}</td>
                                                    <td className="px-4 py-2 border">{item.rateName}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )
                    })
                }
            </div>

        </div>
    )

}
